import { useState, useEffect, useCallback, useRef } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';
import { insurerLabel } from '../lib/insurance';
import { useTranslation } from '../i18n';

/**
 * بیمه — the insurer catalog, this clinic's contracts and coverage rules, one
 * patient's policies, and the booking quote.
 *
 * The catalog is platform-owned and effectively static (a dozen rows), so it is
 * fetched once per session and shared: the registration form, the booking modal
 * and the settle drawer all need it, and three round trips for the same nine rows
 * is exactly the latency the desk cannot afford (design §NFR-3).
 */

const INSURERS = `
  query Insurers {
    insurers { id nameFa nameEn kind taminCode isActive sortOrder }
  }
`;

const PATIENT_POLICIES = `
  query PatientPolicies($patientId: ID!, $activeOnly: Boolean) {
    patientPolicies(patientId: $patientId, activeOnly: $activeOnly) {
      id patientId insurerId insurerName kind policyNumber branchCode
      validFrom validTo isPrimary verification verifiedAt verifiedSource priority
    }
  }
`;

const SET_POLICY = `
  mutation SetPatientPolicy($input: PatientPolicyInput!) {
    setPatientPolicy(input: $input) {
      id patientId insurerId kind policyNumber branchCode isPrimary verification priority
    }
  }
`;

const DELETE_POLICY = `
  mutation DeletePatientPolicy($policyId: ID!) {
    deletePatientPolicy(policyId: $policyId)
  }
`;

const CONTRACTS = `
  query InsuranceContracts($currentOnly: Boolean) {
    insuranceContracts(currentOnly: $currentOnly) {
      id clinicId insurerId insurerName defaultPatientSharePct
      contractNumber effectiveFrom effectiveTo isCurrent createdAt
    }
  }
`;

const SET_CONTRACT = `
  mutation SetInsuranceContract($insurerId: ID!, $pct: Float!, $number: String, $from: Float) {
    setInsuranceContract(
      insurerId: $insurerId, defaultPatientSharePct: $pct,
      contractNumber: $number, effectiveFrom: $from
    ) { id insurerId insurerName defaultPatientSharePct contractNumber isCurrent }
  }
`;

const END_CONTRACT = `
  mutation EndInsuranceContract($insurerId: ID!) {
    endInsuranceContract(insurerId: $insurerId) { id insurerId effectiveTo isCurrent }
  }
`;

const COVERAGE_RULES = `
  query CoverageRules($contractId: ID!) {
    coverageRules(contractId: $contractId) {
      currency
      id contractId catalogId itemType patientSharePct insurerCeilingRial requiresReferral
    }
  }
`;

const SET_RULES = `
  mutation SetCoverageRules($contractId: ID!, $rules: [CoverageRuleInput!]!) {
    setCoverageRules(contractId: $contractId, rules: $rules) {
      currency
      id catalogId itemType patientSharePct insurerCeilingRial requiresReferral
    }
  }
`;

const REORDER_POLICIES = `
  mutation ReorderPatientPolicies($patientId: ID!, $policyIds: [ID!]!) {
    reorderPatientPolicies(patientId: $patientId, policyIds: $policyIds) {
      id insurerId kind priority
    }
  }
`;

// One insurer's part of one line. Requested wherever a share is shown, because a
// bare number the operator has to take on trust is what legs exist to replace.
const LEG_FIELDS = `
  legIndex kind insurerId insurerName policyId contractId
  patientSharePct ceilingAppliedRial referralLetterId referralLetterNumber
  shareRial computedShareRial indirectEstimateRial source
`;

const QUOTE = `
  query InsuranceQuote(
    $patientId: ID!, $doctorId: ID, $services: [PlannedServiceInput!],
    $atTs: Float, $appointmentId: ID, $choice: VisitInsuranceChoiceInput,
    $serviceDeclarationId: ID
  ) {
    insuranceQuote(
      patientId: $patientId, doctorId: $doctorId, services: $services,
      atTs: $atTs, appointmentId: $appointmentId, choice: $choice,
      serviceDeclarationId: $serviceDeclarationId
    ) {
      currency
      totalRial insuranceShareRial patientShareRial
      insurerId insurerName supplementaryInsurerId isContracted source
      needsReferralInsurerIds
      lines {
        catalogId description quantity unitTariffRial totalRial
        insuranceShareRial patientShareRial patientSharePct covered
        coverageMode configVerdictFa indirectEstimateRial
        legs { ${LEG_FIELDS} }
      }
    }
  }
`;

const LETTER_FIELDS = `
  currency id clinicId patientId policyId insurerId insurerName letterNumber
  issuedAt validFrom validTo scopeCatalogIds scopeItemType
  approvedCeilingRial remainingRial patientSharePctOverride singleUse
  fileId status appointmentId consumedByInvoiceId note createdAt
`;

const REFERRAL_LETTERS = `
  query ReferralLetters($patientId: ID, $insurerId: ID, $status: String, $appointmentId: ID) {
    referralLetters(
      patientId: $patientId, insurerId: $insurerId,
      status: $status, appointmentId: $appointmentId
    ) {
      letters { ${LETTER_FIELDS} }
      total
    }
  }
`;

const CREATE_LETTER = `
  mutation CreateReferralLetter($input: ReferralLetterInput!) {
    createReferralLetter(input: $input) { ${LETTER_FIELDS} }
  }
`;

const CANCEL_LETTER = `
  mutation CancelReferralLetter($letterId: ID!, $reason: String!) {
    cancelReferralLetter(letterId: $letterId, reason: $reason) { id status }
  }
`;

const ATTACH_LETTER = `
  mutation AttachReferralLetter($letterId: ID!, $invoiceId: ID!) {
    attachReferralLetter(letterId: $letterId, invoiceId: $invoiceId) {
      currency
      id insuranceShare patientPayable
      lineItems { currency id insuranceShare patientPayable insuranceSource legs: insuranceLegs { ${LEG_FIELDS} } }
    }
  }
`;

// One in-flight promise serves every caller in the session. A FAILURE is never
// cached — the next mount tries again rather than showing an empty select for
// the rest of the session.
let insurersPromise = null;
export function loadInsurers() {
  if (!insurersPromise) {
    insurersPromise = bffQuery(INSURERS)
      .then((d) => d?.insurers || [])
      .catch((err) => { insurersPromise = null; throw err; });
  }
  return insurersPromise;
}

/** Test seam: drop the cached catalog between cases. */
export function __resetInsurerCache() { insurersPromise = null; }

/** The insurer catalog. Never throws — an outage yields an empty list + error. */
export function useInsurers() {
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    loadInsurers()
      .then((rows) => { if (alive) { setInsurers(rows); setError(''); } })
      .catch((e) => { if (alive) setError(e?.message || 'insurers'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const byId = useCallback(
    (id) => insurers.find((i) => i.id === id) || null,
    [insurers],
  );

  return { insurers, byId, loading, error };
}

/**
 * `nameOf(insurerId, fallback)` — an insurer's name in the panel's language.
 *
 * Rows from the BFF carry `insurerName`, which it resolves from `name_fa`: it has
 * no idea what language the reader has chosen. The id travels with every such
 * row, so the name is looked up again here, in the catalog, and the server's
 * string is only the fallback for an id the catalog does not hold.
 */
export function useInsurerName() {
  const { locale } = useTranslation();
  const { byId } = useInsurers();
  return useCallback(
    (insurerId, fallback) => insurerLabel(byId(insurerId), locale) || fallback || insurerId || '',
    [byId, locale],
  );
}

/** One patient's policies, with save/delete. */
export function usePatientPolicies(patientId, { activeOnly = false } = {}) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(!!patientId);
  const [error, setError] = useState('');
  const reqId = useRef(0);

  const reload = useCallback(async () => {
    if (!patientId) { setPolicies([]); setLoading(false); return; }
    const mine = ++reqId.current;
    setLoading(true);
    try {
      const d = await bffQuery(PATIENT_POLICIES, { patientId, activeOnly });
      // A slower earlier request must not overwrite a newer answer.
      if (mine === reqId.current) { setPolicies(d?.patientPolicies || []); setError(''); }
    } catch (e) {
      if (mine === reqId.current) setError(e?.message || 'policies');
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [patientId, activeOnly]);

  useEffect(() => { reload(); }, [reload]);

  const savePolicy = useCallback(async (input) => {
    const d = await bffMutation(SET_POLICY, { input: { ...input, patientId } });
    await reload();
    return d?.setPatientPolicy || null;
  }, [patientId, reload]);

  const removePolicy = useCallback(async (policyId) => {
    await bffMutation(DELETE_POLICY, { policyId });
    await reload();
  }, [reload]);

  const reorder = useCallback(async (policyIds) => {
    await bffMutation(REORDER_POLICIES, { patientId, policyIds });
    await reload();
  }, [patientId, reload]);

  const basic = policies.find((p) => p.kind === 'basic' && p.isPrimary) || null;
  // EVERY supplementary, in the order they are applied. They pay in sequence,
  // each on what the previous one left, so a screen that reads only the first
  // silently shows the patient fewer insurers than they hold.
  const supplementaries = policies
    .filter((p) => p.kind === 'supplementary')
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));

  return {
    policies,
    basic,
    supplementaries,
    // DEPRECATED — the first supplementary, for a caller that predates the list.
    supplementary: supplementaries[0] || null,
    loading,
    error,
    reload,
    savePolicy,
    removePolicy,
    reorder,
  };
}

/**
 * معرفی‌نامه‌های one patient holds at this clinic.
 *
 * Read wherever a missing letter can be fixed: the patient drawer, the booking
 * modal, and check-in — which is where it is usually discovered.
 */
export function useReferralLetters(patientId, { status, appointmentId, enabled = true } = {}) {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reqId = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled || !patientId) { setLetters([]); setLoading(false); return; }
    const mine = ++reqId.current;
    setLoading(true);
    try {
      const d = await bffQuery(REFERRAL_LETTERS, {
        patientId, status: status || null, appointmentId: appointmentId || null,
      });
      if (mine === reqId.current) { setLetters(d?.referralLetters?.letters || []); setError(''); }
    } catch (e) {
      if (mine === reqId.current) setError(e?.message || 'letters');
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [patientId, status, appointmentId, enabled]);

  useEffect(() => { reload(); }, [reload]);

  const create = useCallback(async (input) => {
    const d = await bffMutation(CREATE_LETTER, { input: { ...input, patientId } });
    await reload();
    return d?.createReferralLetter || null;
  }, [patientId, reload]);

  const cancel = useCallback(async (letterId, reason) => {
    await bffMutation(CANCEL_LETTER, { letterId, reason });
    await reload();
  }, [reload]);

  // Returns the RE-PRICED invoice: the letter only matters through the numbers
  // it changes, and the screen that attached it is waiting to show them.
  const attach = useCallback(async (letterId, invoiceId) => {
    const d = await bffMutation(ATTACH_LETTER, { letterId, invoiceId });
    await reload();
    return d?.attachReferralLetter || null;
  }, [reload]);

  return { letters, loading, error, reload, create, cancel, attach };
}

/** This clinic's insurer agreements and their coverage rules. */
export function useInsuranceContracts({ currentOnly = false } = {}) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await bffQuery(CONTRACTS, { currentOnly });
      setContracts(d?.insuranceContracts || []);
      setError('');
    } catch (e) {
      setError(e?.message || 'contracts');
    } finally {
      setLoading(false);
    }
  }, [currentOnly]);

  useEffect(() => { reload(); }, [reload]);

  const saveContract = useCallback(async ({ insurerId, pct, number, from }) => {
    const d = await bffMutation(SET_CONTRACT, {
      insurerId, pct: Number(pct), number: number || null, from: from || null,
    });
    await reload();
    return d?.setInsuranceContract || null;
  }, [reload]);

  const endContract = useCallback(async (insurerId) => {
    await bffMutation(END_CONTRACT, { insurerId });
    await reload();
  }, [reload]);

  const loadRules = useCallback(async (contractId) => {
    const d = await bffQuery(COVERAGE_RULES, { contractId });
    return d?.coverageRules || [];
  }, []);

  const saveRules = useCallback(async (contractId, rules) => {
    const d = await bffMutation(SET_RULES, {
      contractId,
      rules: (rules || []).map((r) => ({
        id: r.id || null,
        catalogId: r.catalogId || null,
        itemType: r.itemType || null,
        patientSharePct: Number(r.patientSharePct) || 0,
        insurerCeilingRial: r.insurerCeilingRial ? Number(r.insurerCeilingRial) : null,
        requiresReferral: !!r.requiresReferral,
      })),
    });
    return d?.setCoverageRules || [];
  }, []);

  return { contracts, loading, error, reload, saveContract, endContract, loadRules, saveRules };
}

/**
 * The booking quote — «سهم بیمار» before the appointment exists.
 *
 * Debounced against the service picker: every chip toggle would otherwise fire a
 * quote, and the desk only ever sees the last one.
 */
export function useInsuranceQuote({
  patientId, doctorId, services, atTs, appointmentId, choice,
  serviceDeclarationId = null, enabled = true,
}) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const key = JSON.stringify({
    patientId, doctorId, services, atTs, appointmentId, choice, serviceDeclarationId,
  });

  useEffect(() => {
    if (!enabled || !patientId) { setQuote(null); return undefined; }
    const mine = ++reqId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      bffQuery(QUOTE, {
        patientId, doctorId, services, atTs, appointmentId, choice: choice || null,
        // The PRIMARY service, so the preview is the whole bill and not only the
        // extras beside it (§S1).
        serviceDeclarationId: serviceDeclarationId || null,
      })
        .then((d) => { if (mine === reqId.current) setQuote(d?.insuranceQuote || null); })
        // A quote is advisory: its failure must never block a booking, so the
        // chip and the split simply do not appear.
        .catch(() => { if (mine === reqId.current) setQuote(null); })
        .finally(() => { if (mine === reqId.current) setLoading(false); });
    }, 250);
    return () => { clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { quote, loading };
}
